// ═══════════════════════════════════════════════════════════════════════════
//  MESIN STEM — vokal on/off, musik on/off, nada ±6, tempo, volume (3 Okt 2026)
//
//  Untuk lagu UPLOAD yang sudah diproses (kr_song_file mutu 'instrumen' +
//  'vokal', jenis 'audio'). Tanpa pustaka luar: Web Audio + AudioWorklet yang
//  ditulis sendiri.
//
//  ── TIGA MODE, MENURUT BERKAS YANG ADA ───────────────────────────────────
//
//    stem  : instrumen + vokal tersedia. Dua stem diputar bersamaan; vokal
//            dan musik masing-masing punya gain sendiri (ramp pendek, tanpa
//            klik). Kalau ada VIDEO, video jadi jam utama dan DIBISUKAN —
//            suaranya datang dari stem yang disinkronkan ke video.currentTime.
//    tukar : hanya instrumen (tanpa stem vokal). Berkas asli + instrumen
//            diputar bersamaan; vokal on/off = silang antar keduanya.
//            Musik off = bisu total (vokal tidak bisa dipisah).
//    asli  : belum diproses. Hanya berkas asli; tombol vokal mati
//            ("belum diproses"), musik off = bisu.
//
//  ── KENAPA ELEMEN MEDIA, BUKAN decodeAudioData ──────────────────────────
//
//  decodeAudioData menuntut seluruh berkas diunduh dulu (TV menunggu) dan
//  memakan ±90 MB RAM per stem 4 menit — terlalu berat untuk TV box Android.
//  AudioBufferSourceNode juga tidak bisa mengubah tempo tanpa mengubah nada.
//  Elemen media mengalir (streaming), punya preservesPitch, dan selisihnya
//  dijaga pengikut: koreksi halus lewat playbackRate (±3%, tidak terdengar
//  karena preservesPitch) dan lompat posisi kalau selisih > 80 ms.
//
//  ── PENGGESER NADA ───────────────────────────────────────────────────────
//
//  Dua titik baca pada satu garis tunda (dual delay-line), disilangkan dengan
//  bobot sin / |cos| yang kuadratnya berjumlah satu (keras suara rata), dan
//  bobot tiap titik tepat nol saat ia melompat (tidak ada klik). Tempo tidak
//  berubah karena titik tulis berjalan normal; hanya laju baca yang dikali
//  rasio 2^(semitone/12). Rasio dihaluskan per sampel supaya tombol +/−
//  tidak berbunyi "patah". Nada 0 MELEWATI worklet sepenuhnya.
// ═══════════════════════════════════════════════════════════════════════════

const KODE_GESER = `
class GeserNadaStem extends AudioWorkletProcessor {
  static get parameterDescriptors() {
    return [{ name: 'rasio', defaultValue: 1, minValue: 0.5, maxValue: 2, automationRate: 'k-rate' }]
  }
  constructor() {
    super()
    // ~60 ms. Lebih pendek bergetar, lebih panjang terasa seperti gema.
    this.N = Math.max(1024, Math.round(sampleRate * 0.06))
    this.buf = [new Float32Array(this.N), new Float32Array(this.N)]
    this.tulis = 0
    this.fase = 0
    this.r = 1
  }
  baca(b, p) {
    const N = this.N
    while (p < 0) p += N
    while (p >= N) p -= N
    const i0 = Math.floor(p), f = p - i0
    const i1 = i0 + 1 === N ? 0 : i0 + 1
    return b[i0] * (1 - f) + b[i1] * f
  }
  process(inputs, outputs, params) {
    const inp = inputs[0], out = outputs[0]
    if (!out || out.length === 0) return true
    const target = params.rasio.length > 0 ? params.rasio[0] : 1
    const N = this.N, D = N - 2
    const n = out[0].length
    const nch = Math.min(out.length, 2)
    const ada = inp && inp.length > 0
    for (let i = 0; i < n; i++) {
      this.r += (target - this.r) * 0.0008
      for (let ch = 0; ch < 2; ch++) {
        const x = ada ? (inp[ch] || inp[0]) : null
        this.buf[ch][this.tulis] = x ? x[i] : 0
      }
      const f1 = this.fase
      let f2 = f1 + 0.5
      if (f2 >= 1) f2 -= 1
      const w1 = Math.sin(Math.PI * f1)
      const w2 = Math.sin(Math.PI * f2)
      const p1 = this.tulis - f1 * D
      const p2 = this.tulis - f2 * D
      for (let ch = 0; ch < nch; ch++) {
        const b = this.buf[ch]
        out[ch][i] = this.baca(b, p1) * w1 + this.baca(b, p2) * w2
      }
      this.tulis = this.tulis + 1 === N ? 0 : this.tulis + 1
      this.fase += (1 - this.r) / D
      while (this.fase < 0) this.fase += 1
      while (this.fase >= 1) this.fase -= 1
    }
    for (let ch = nch; ch < out.length; ch++) out[ch].set(out[0])
    return true
  }
}
registerProcessor('geser-nada-stem', GeserNadaStem)
`

//    kanal : (26 Okt 2026) video karaoke vendor lama — SATU berkas, vokal di
//            satu kanal stereo (mis. kiri = lagu bervokal), kanan = musik saja.
//            Vokal on/off = pilih kanal mana yang dikirim ke kedua speaker.
export type ModeAudio = 'stem' | 'tukar' | 'asli' | 'kanal'

export type Kemampuan = {
  mode: ModeAudio
  vokalBisa: boolean
  musikBisa: boolean
  nadaBisa: boolean
  tempoBisa: boolean
}

type Peran = 'inst' | 'voc' | 'utama'

type MediaPP = HTMLMediaElement & {
  preservesPitch?: boolean; webkitPreservesPitch?: boolean; mozPreservesPitch?: boolean
}

function tahanNada(m: MediaPP) {
  m.preservesPitch = true
  m.webkitPreservesPitch = true
  m.mozPreservesPitch = true
}

export class MesinStem {
  readonly mode: ModeAudio
  kemampuan: Kemampuan

  private ctx: AudioContext
  private master: MediaPP
  private pengikut: MediaPP[] = []
  private gain: Partial<Record<Peran, GainNode>> = {}
  private bus: GainNode
  private lewat: GainNode
  private masukGeser: GainNode
  private keluar: GainNode
  private vol: GainNode
  private geser: AudioWorkletNode | null = null
  private jamSinkron: number | undefined
  private lepas: (() => void)[] = []

  private vokal = false
  private ukurL: AnalyserNode | null = null
  private ukurR: AnalyserNode | null = null

  /** 26 Okt: RMS kanal kiri/kanan (0–1) + korelasi (1 = isi sama persis), selama `ms`. */
  async ukurKanal(ms = 3000): Promise<{ l: number; r: number; kor: number } | null> {
    const aL = this.ukurL, aR = this.ukurR
    if (!aL || !aR) return null
    const n = aL.fftSize, bl = new Float32Array(n), br = new Float32Array(n)
    let sl = 0, sr = 0, slr = 0, k = 0
    const akhir = performance.now() + ms
    while (performance.now() < akhir) {
      aL.getFloatTimeDomainData(bl); aR.getFloatTimeDomainData(br)
      for (let i = 0; i < n; i++) { sl += bl[i] * bl[i]; sr += br[i] * br[i]; slr += bl[i] * br[i] }
      k += n
      await new Promise(r => setTimeout(r, 100))
    }
    if (!k) return null
    const l = Math.sqrt(sl / k), r = Math.sqrt(sr / k)
    const kor = sl > 0 && sr > 0 ? slr / Math.sqrt(sl * sr) : 0
    return { l: Math.round(l * 1000) / 1000, r: Math.round(r * 1000) / 1000, kor: Math.round(kor * 100) / 100 }
  }
  private musik = true
  private nada = 0

  /**
   * @param master    elemen <video> di layar (jam utama lirik & skor)
   * @param mode      stem | tukar | asli
   * @param peranMaster peran suara master: 'inst' | 'utama' | null (bisu — video
   *                  dalam mode stem, suaranya dari stem)
   * @param ikut      stem pengikut: url + peran
   */
  constructor(opt: {
    master: HTMLMediaElement
    mode: ModeAudio
    peranMaster: Peran | null
    ikut: { url: string; peran: Peran }[]
    kanalVokal?: 'L' | 'R' | null
  }) {
    this.mode = opt.mode
    this.master = opt.master as MediaPP
    const AC: typeof AudioContext =
      window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext
    this.ctx = new AC()

    this.bus = this.ctx.createGain()
    this.lewat = this.ctx.createGain()
    this.masukGeser = this.ctx.createGain()
    this.keluar = this.ctx.createGain()
    this.vol = this.ctx.createGain()
    this.masukGeser.gain.value = 0
    this.bus.connect(this.lewat)
    this.lewat.connect(this.keluar)
    this.keluar.connect(this.vol)
    // 21 Okt 2026: volume boleh sampai 150 (boost). Limiter di ujung rantai
    // menahan puncak di -1 dBFS supaya boost tidak jadi "kresek" (clipping).
    // Di volume normal (≤100) hampir tidak pernah bekerja.
    // 22 Okt: limiter dilunakkan (knee 6 dB, rilis 0,25 dtk) — versi keras
    // (knee 0, rilis 0,12) "memompa" dan terdengar kresek saat volume dinaikkan.
    const batas = this.ctx.createDynamicsCompressor()
    batas.threshold.value = -3
    batas.knee.value = 6
    batas.ratio.value = 12
    batas.attack.value = 0.005
    batas.release.value = 0.25
    this.vol.connect(batas)
    batas.connect(this.ctx.destination)

    const sambung = (el: HTMLMediaElement, peran: Peran) => {
      const src = this.ctx.createMediaElementSource(el)
      const g = this.ctx.createGain()
      src.connect(g)
      g.connect(this.bus)
      this.gain[peran] = g
    }

    tahanNada(this.master)
    if (opt.mode === 'kanal') {
      // Pecah stereo: kanal vokal → gain 'voc', kanal lain → gain 'inst'.
      // Keduanya mono; bus/destination menyebarkannya ke dua speaker.
      this.master.muted = false
      const src = this.ctx.createMediaElementSource(this.master)
      const pecah = this.ctx.createChannelSplitter(2)
      src.connect(pecah)
      const iVok = opt.kanalVokal === 'R' ? 1 : 0
      const gv = this.ctx.createGain(), gi = this.ctx.createGain()
      pecah.connect(gv, iVok)
      pecah.connect(gi, 1 - iVok)
      gv.connect(this.bus); gi.connect(this.bus)
      // 26 Okt: pengukur per kanal — memastikan suara benar-benar masuk Web Audio
      // (bukan senyap karena CORS) dan kedua kanal memang berbeda isinya.
      this.ukurL = this.ctx.createAnalyser(); this.ukurR = this.ctx.createAnalyser()
      this.ukurL.fftSize = 2048; this.ukurR.fftSize = 2048
      pecah.connect(this.ukurL, 0); pecah.connect(this.ukurR, 1)
      this.gain.voc = gv
      this.gain.inst = gi
    } else if (opt.peranMaster) {
      this.master.muted = false
      sambung(this.master, opt.peranMaster)
    } else {
      // Video dalam mode stem: gambar saja. Suaranya dari stem.
      this.master.muted = true
    }
    for (const s of opt.ikut) {
      const a = new Audio() as MediaPP
      a.crossOrigin = 'anonymous'
      a.preload = 'auto'
      a.src = s.url
      tahanNada(a)
      sambung(a, s.peran)
      this.pengikut.push(a)
    }

    this.kemampuan = {
      mode: opt.mode,
      vokalBisa: opt.mode !== 'asli',
      musikBisa: true,
      nadaBisa: false,
      tempoBisa: 'preservesPitch' in this.master || 'webkitPreservesPitch' in this.master,
    }
    this.terapkanGain(0)
    this.pasangSinkron()
  }

  /** Memuat worklet. Gagal = nada tidak bisa digeser, lagu tetap jalan. */
  async siapkan(): Promise<Kemampuan> {
    try {
      const url = URL.createObjectURL(new Blob([KODE_GESER], { type: 'application/javascript' }))
      await this.ctx.audioWorklet.addModule(url)
      URL.revokeObjectURL(url)
      this.geser = new AudioWorkletNode(this.ctx, 'geser-nada-stem', {
        numberOfInputs: 1, numberOfOutputs: 1, outputChannelCount: [2],
        channelCount: 2, channelCountMode: 'explicit',
      })
      this.bus.connect(this.masukGeser)
      this.masukGeser.connect(this.geser)
      this.geser.connect(this.keluar)
      this.kemampuan.nadaBisa = true
      this.setNada(this.nada)
    } catch {
      // AudioWorklet butuh konteks aman (https / localhost).
      this.kemampuan.nadaBisa = false
    }
    return this.kemampuan
  }

  /** true = suara benar-benar boleh jalan (konteks tidak tertahan peramban). */
  async mulai(): Promise<boolean> {
    try { if (this.ctx.state === 'suspended') await this.ctx.resume() } catch { /* tertahan */ }
    try { await this.master.play() } catch { return false }
    // 6 Okt: autoplay dicoba tanpa sentuhan. Video boleh jalan tapi suaranya
    // (AudioContext) ditahan → hentikan lagi, supaya lagu tidak berjalan bisu
    // sementara layar menunggu tombol "Tekan untuk mulai".
    if (this.ctx.state !== 'running') {
      try { this.master.pause(); this.master.currentTime = 0 } catch { /* */ }
      return false
    }
    return true
  }

  /** 8 Okt 2026 — tombol ulang (loop): kembali ke awal lagu, stem ikut.
   *  Pengikut yang sudah `ended` dilewati sinkron biasa, jadi di-reset di sini. */
  ulangDariAwal() {
    // 22 Okt: "awal" = titik lewati-intro (lihat setMulai), bukan detik 0.
    const t0 = this.mulaiDetik
    try { this.master.currentTime = t0 } catch { /* belum siap */ }
    for (const a of this.pengikut) { try { a.currentTime = t0 } catch { /* */ } }
    if (this.ctx.state === 'suspended') this.ctx.resume().catch(() => {})
    this.master.play().catch(() => {})
    for (const a of this.pengikut) a.play().catch(() => {})
  }

  jeda(ya: boolean) {
    if (ya) { this.master.pause(); return }
    if (this.ctx.state === 'suspended') this.ctx.resume().catch(() => {})
    this.master.play().catch(() => {})
  }

  /** 22 Okt 2026: lagu dimulai dari detik ini (lewati intro cerita video). */
  private mulaiDetik = 0
  setMulai(detik: number) {
    this.mulaiDetik = Math.max(0, detik || 0)
    if (this.mulaiDetik > 0 && this.master.currentTime < this.mulaiDetik) {
      try { this.master.currentTime = this.mulaiDetik } catch { /* belum siap — dicoba lagi di loadedmetadata */ }
      for (const a of this.pengikut) { try { a.currentTime = this.mulaiDetik } catch { /* */ } }
    }
  }

  setVokal(nyala: boolean) { this.vokal = nyala; this.terapkanGain(0.04) }
  setMusik(nyala: boolean) { this.musik = nyala; this.terapkanGain(0.04) }

  setVolume(persen: number) {
    // 0–100: kurva kuadrat (100 = 1×). 22 Okt: di atas 100 naik LINEAR —
    // 150 = 1,5× (+3,5 dB), bukan 2,25×: boost besar memaksa limiter bekerja
    // terus dan itu yang terdengar "kresek".
    const v = Math.max(0, Math.min(150, persen)) / 100
    const g = v <= 1 ? v * v : v
    this.vol.gain.setTargetAtTime(g, this.ctx.currentTime, 0.05)
  }

  setNada(semitone: number) {
    this.nada = Math.max(-6, Math.min(6, Math.round(semitone)))
    if (!this.geser) return
    const t = this.ctx.currentTime
    if (this.nada === 0) {
      this.lewat.gain.setTargetAtTime(1, t, 0.03)
      this.masukGeser.gain.setTargetAtTime(0, t, 0.03)
      return
    }
    const p = this.geser.parameters.get('rasio')
    if (p) p.setValueAtTime(Math.pow(2, this.nada / 12), t)
    this.lewat.gain.setTargetAtTime(0, t, 0.03)
    this.masukGeser.gain.setTargetAtTime(1, t, 0.03)
  }

  setTempo(persen: number) {
    if (!this.kemampuan.tempoBisa) return
    tahanNada(this.master)
    this.master.playbackRate = Math.min(1.3, Math.max(0.7, persen / 100))
  }

  tutup() {
    window.clearInterval(this.jamSinkron)
    this.lepas.forEach(f => f())
    for (const a of this.pengikut) {
      try { a.pause(); a.removeAttribute('src'); a.load() } catch { /* sudah lepas */ }
    }
    this.ctx.close().catch(() => {})
  }

  // ── gain per peran ──
  private terapkanGain(tau: number) {
    const t = this.ctx.currentTime
    const atur = (p: Peran, v: number) => {
      const g = this.gain[p]
      if (!g) return
      if (tau <= 0) g.gain.value = v
      else g.gain.setTargetAtTime(v, t, tau)
    }
    const m = this.musik ? 1 : 0
    if (this.mode === 'stem') {
      atur('inst', m)
      atur('voc', this.vokal ? 1 : 0)
    } else if (this.mode === 'kanal') {
      // Kanal vokal = lagu bervokal; kanal lain = musik saja (minus one).
      atur('voc', this.vokal ? m : 0)
      atur('inst', this.vokal ? 0 : m)
    } else if (this.mode === 'tukar') {
      // Berkas asli BERVOKAL; instrumen TANPA vokal. Musik mati = bisu.
      atur('utama', this.vokal ? m : 0)
      atur('inst', this.vokal ? 0 : m)
    } else {
      atur('utama', m)
    }
  }

  // ── sinkron pengikut ke jam master ──
  //
  // 22 Okt 2026 — Zaldy: "masih kresek-kresek suaranya, video patah-patah".
  // Dulu tiap pengikut dikoreksi lewat playbackRate ±3% (Firefox menjalankan
  // time-stretch → suara bergelombang/kresek) dan dilompatkan tiap selisih
  // > 80 ms dari VIDEO — video yang tersendat (PC TV berat mendekode)
  // membuat stem terus melompat = bunyi patah. Sekarang:
  //   · tidak ada koreksi laju; laju pengikut selalu = laju master;
  //   · video bisu (mode stem) → stem pertama (instrumen) yang mengikuti
  //     video dengan toleransi 150 ms; stem lain mengikuti INSTRUMEN (sesama
  //     audio, jamnya sama) dengan toleransi 50 ms — vokal & musik tetap rapat;
  //   · play/playing tidak lagi memaksa lompat bila selisihnya masih wajar.
  private acuan(i: number): { ref: HTMLMediaElement; batas: number } {
    const m = this.master
    if (m.muted && i > 0 && this.pengikut.length > 1) return { ref: this.pengikut[0], batas: 0.05 }
    return { ref: m, batas: m.muted ? 0.15 : 0.08 }
  }

  private pasangSinkron() {
    const m = this.master
    const samakan = (paksa: boolean) => {
      this.pengikut.forEach((a, i) => {
        if (a.ended) return
        const { ref, batas } = this.acuan(i)
        try {
          if (!isNaN(a.duration) && ref.currentTime >= a.duration) return
          if (paksa || Math.abs(a.currentTime - ref.currentTime) > batas) a.currentTime = ref.currentTime
        } catch { /* belum siap */ }
        a.playbackRate = m.playbackRate
      })
    }
    const putar = () => {
      samakan(false)
      for (const a of this.pengikut) a.play().catch(() => {})
    }
    const henti = () => { for (const a of this.pengikut) a.pause() }
    const laju = () => { for (const a of this.pengikut) a.playbackRate = m.playbackRate }
    const daftar: [string, () => void][] = [
      ['play', putar], ['playing', putar], ['pause', henti], ['waiting', henti],
      ['ended', henti], ['seeked', () => samakan(true)], ['ratechange', laju],
    ]
    for (const [ev, f] of daftar) {
      m.addEventListener(ev, f)
      this.lepas.push(() => m.removeEventListener(ev, f))
    }
    this.jamSinkron = window.setInterval(() => {
      if (m.paused || m.seeking || m.readyState < 2) return
      this.pengikut.forEach((a, i) => {
        if (a.ended || a.seeking || a.readyState < 2) return
        const { ref, batas } = this.acuan(i)
        if (ref !== m && (ref.seeking || ref.readyState < 2)) return
        if (!isNaN(a.duration) && ref.currentTime >= a.duration) return
        if (Math.abs(a.currentTime - ref.currentTime) > batas) {
          try { a.currentTime = ref.currentTime } catch { /* abaikan */ }
        }
        if (a.playbackRate !== m.playbackRate) a.playbackRate = m.playbackRate
        if (a.paused) a.play().catch(() => {})
      })
    }, 250)
  }
}
